"use client"

import { useLayoutEffect, useRef, useState } from "react"
import type { ReactNode } from "react"

type Layer = {
  id: number
  value: string
  children: ReactNode
  animate: boolean
}

/** Crossfades only the changed screen content, preserving opacity during quick successive updates. */
export function EcobeeDisplayTransition({
  value, children, paused, reducedMotion,
}: {
  value: string
  children: ReactNode
  paused: boolean
  reducedMotion: boolean
}) {
  const [layers, setLayers] = useState<Layer[]>([{
    id: 0, value, children, animate: false,
  }])
  const nodes = useRef(new Map<number, SVGGElement>())
  const animations = useRef(new Map<number, { animation: Animation, target: number }>())
  const previousChildren = useRef(children)
  // The current layer is always retained; only outgoing layers are removed.
  const latest = layers[layers.length - 1]!

  // Retain the outgoing drawing without remounting the device or its touch controls.
  if (latest.value !== value) {
    const outgoing = layers.slice(-2).map((layer) => layer.id === latest.id
      ? { ...layer, children: previousChildren.current }
      : layer)
    setLayers([...outgoing, {
      id: latest.id + 1, value, children, animate: !paused,
    }])
  }

  useLayoutEffect(() => {
    previousChildren.current = children
  })

  useLayoutEffect(() => {
    for (const [id, { animation }] of animations.current) {
      if (!layers.some((layer) => layer.id === id)) {
        animation.cancel()
        animations.current.delete(id)
      }
    }
    for (const layer of layers) {
      const node = nodes.current.get(layer.id)
      if (!node) {
        continue
      }
      const target = layer.id === latest.id ? 1 : 0
      const running = animations.current.get(layer.id)
      if (running?.target === target && !reducedMotion) {
        continue
      }

      const opacity = Number.parseFloat(getComputedStyle(node).opacity)
      running?.animation.cancel()
      animations.current.delete(layer.id)
      node.style.opacity = String(opacity)

      const settle = () => {
        node.style.opacity = String(target)
        if (target === 0) {
          setLayers((current) => current.filter(({ id }) => id !== layer.id))
        }
      }

      if (reducedMotion || !latest.animate || !node.animate || opacity === target) {
        settle()
        continue
      }

      const animation = node.animate([{ opacity }, { opacity: target }], {
        duration: 760,
        easing: "cubic-bezier(.22, 1, .36, 1)",
        fill: "forwards",
      })
      animations.current.set(layer.id, { animation, target })
      if (paused) {
        animation.pause()
      }
      void animation.finished.then(() => {
        if (animations.current.get(layer.id)?.animation !== animation) {
          return
        }
        settle()
        animation.cancel()
        animations.current.delete(layer.id)
      }).catch(() => {
        // A newer update can interrupt this fade.
      })
    }
  }, [layers, latest.id, latest.animate, paused, reducedMotion])

  useLayoutEffect(() => {
    for (const { animation } of animations.current.values()) {
      if (paused) {
        animation.pause()
      }
      else {
        animation.play()
      }
    }
  }, [paused])

  useLayoutEffect(() => {
    const currentAnimations = animations.current
    return () => {
      for (const { animation } of currentAnimations.values()) {
        animation.cancel()
      }
      currentAnimations.clear()
    }
  }, [])

  return (
    <g data-ecobee-transition={value}>
      {layers.map((layer) => (
        <g
          key={layer.id}
          data-ecobee-layer={layer.id}
          data-current={layer.id === latest.id}
          ref={(node) => {
            if (node) {
              nodes.current.set(layer.id, node)
            }
            else {
              nodes.current.delete(layer.id)
            }
          }}
          style={{ opacity: layer.id === 0 ? 1 : 0 }}
        >
          {layer.id === latest.id ? children : layer.children}
        </g>
      ))}
    </g>
  )
}
